import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProgramme } from '@/lib/api/hooks/programme';
import { Loader } from '@/components/ui/Loader';
import { ArticleContent } from '@/components/ui/ArticleContent';

const ProgrammeContentView: React.FC = () => {
  const { id: programmeId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!programmeId) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Programme ID missing</h2>
        <button onClick={() => navigate('/admin/programmes')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Programmes
        </button>
      </div>
    );
  }

  const { data: programmeData, isLoading, error } = useProgramme(programmeId);
  const article = programmeData?.data;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <Loader size="md" variant="wine" message="Loading programme..." />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Programme not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The programme could not be found'}</p>
        <button onClick={() => navigate('/admin/programmes')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Programmes
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => navigate('/admin/programmes')}>Programmes</span>
        <span className="mx-2">&gt;</span>
        <span className="cursor-pointer hover:underline" onClick={() => navigate(`/admin/programmes/${programmeId}`)}>{article.title}</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">Content</span>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between border-b border-gray-200 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{article.title}</h1>
            <p className="text-sm text-gray-500">
              {new Date(article.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })} | By {article.createdByUsername || article.createdBy}
            </p>
          </div>
          <button
            onClick={() => navigate(`/admin/programmes/${programmeId}`)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            &larr; Back to Detail
          </button>
        </div>

        {/* Content Section */}
        <div className="border-t border-gray-200 pt-6">
          <ArticleContent content={article.content} className="text-gray-700" />
        </div>
      </div>
    </div>
  );
};

export default ProgrammeContentView;

